<template>
  <div class="not-found-page">
    <!-- Animated Background -->
    <div class="animated-bg">
      <div class="floating-shape shape-1"></div>
      <div class="floating-shape shape-2"></div>
      <div class="floating-shape shape-3"></div>
      <div class="floating-shape shape-4"></div>
      <div class="floating-shape shape-5"></div>
    </div>

    <!-- Main Content -->
    <div class="container">
      <div class="content-wrapper" data-aos="fade-up" data-aos-duration="800">
        <!-- 404 Number with Glitch Effect -->
        <div class="error-code" data-aos="zoom-in" data-aos-delay="200">
          <span class="glitch" data-text="404">404</span>
        </div>

        <!-- Main Message -->
        <div class="message-section" data-aos="fade-up" data-aos-delay="400">
          <h1 class="main-title">Oops! Page Not Found</h1>
          <p class="subtitle">Looks like you've wandered off the summit trail!</p>
        </div>

        <!-- Interactive Elements -->
        <div class="interactive-section" data-aos="fade-up" data-aos-delay="600">
          <div class="icon-bounce">
            <svg width="80" height="80" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 2L22 22H2L12 2Z" fill="#4ade80" stroke="#22c55e" stroke-width="1" stroke-linejoin="round"/>
              <circle cx="12" cy="8" r="2" fill="#16a34a"/>
              <path d="M8 16L16 16" stroke="#16a34a" stroke-width="2" stroke-linecap="round"/>
            </svg>
          </div>
          <p class="description">
            Don't worry, even the best explorers take wrong turns. Let's get you back to the Africa HR Summit!
          </p>
        </div>

        <!-- Action Buttons -->
        <div class="action-buttons" data-aos="fade-up" data-aos-delay="800">
          <router-link to="/" class="btn-primary">
            <span class="btn-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M3 9L12 2L21 9V20C21 20.5523 20.5523 21 20 21H4C3.44772 21 3 20.5523 3 20V9Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                <polyline points="9,22 9,12 15,12 15,22" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </span>
            Back to Home
          </router-link>
          <button @click="goBack" class="btn-secondary">
            <span class="btn-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M19 12H5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M12 19L5 12L12 5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </span>
            Go Back
          </button>
        </div>

        <!-- Fun Facts -->
        <div class="fun-section" data-aos="fade-up" data-aos-delay="1000">
          <div class="fun-fact">
            <span class="fact-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="10" stroke="#4ade80" stroke-width="2"/>
                <path d="M9.09 9C9.3251 8.33167 9.78915 7.76811 10.4 7.40913C11.0108 7.05016 11.7289 6.91894 12.4272 7.03871C13.1255 7.15849 13.7588 7.52152 14.2151 8.06353C14.6713 8.60553 14.9211 9.29152 14.92 10C14.92 12 11.92 13 11.92 13" stroke="#4ade80" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M12 17H12.01" stroke="#4ade80" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </span>
            <span class="fact-text">Fun fact: You're still awesome even when lost!</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Particles Animation -->
    <div class="particles">
      <div class="particle" v-for="n in 20" :key="n"></div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'NotFoundView',
  data() {
    return {
      sparkleInterval: null
    }
  },
  mounted() {
    // Add some interactive sparkle effects
    this.createSparkles();
  },
  beforeUnmount() {
    // Clean up interval when component is destroyed
    if (this.sparkleInterval) {
      clearInterval(this.sparkleInterval);
      this.sparkleInterval = null;
    }
  },
  methods: {
    goBack() {
      if (window.history.length > 1) {
        this.$router.go(-1);
      } else {
        this.$router.push('/');
      }
    },
    createSparkles() {
      // Add random sparkle animations
      this.sparkleInterval = setInterval(() => {
        const container = this.$el;
        if (!container) return; // Safety check

        const sparkle = document.createElement('div');
        sparkle.className = 'sparkle';
        sparkle.style.left = Math.random() * 100 + '%';
        sparkle.style.top = Math.random() * 100 + '%';
        container.appendChild(sparkle);

        setTimeout(() => {
          if (sparkle.parentNode && container.contains(sparkle)) {
            sparkle.parentNode.removeChild(sparkle);
          }
        }, 2000);
      }, 3000);
    }
  }
}
</script>

<style scoped>
.not-found-page {
  min-height: 100vh;
  position: relative;
  overflow: hidden;
  background: linear-gradient(
    135deg,
    #0a0a23 0%,
    #1a472a 25%,
    #2d5f3f 50%,
    #0f2419 75%,
    #000000 100%
  );
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: 'Inter', sans-serif;
}

/* Animated Background */
.animated-bg {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
}

.floating-shape {
  position: absolute;
  background: linear-gradient(45deg, rgba(74, 222, 128, 0.1), rgba(34, 197, 94, 0.15));
  border-radius: 50%;
  animation: float 6s ease-in-out infinite;
}

.shape-1 {
  width: 80px;
  height: 80px;
  top: 20%;
  left: 10%;
  animation-delay: 0s;
}

.shape-2 {
  width: 120px;
  height: 120px;
  top: 60%;
  right: 15%;
  animation-delay: 2s;
}

.shape-3 {
  width: 60px;
  height: 60px;
  top: 80%;
  left: 20%;
  animation-delay: 4s;
}

.shape-4 {
  width: 100px;
  height: 100px;
  top: 30%;
  right: 30%;
  animation-delay: 1s;
}

.shape-5 {
  width: 140px;
  height: 140px;
  top: 10%;
  right: 50%;
  animation-delay: 3s;
}

@keyframes float {
  0%, 100% {
    transform: translateY(0px) rotate(0deg);
  }
  33% {
    transform: translateY(-20px) rotate(120deg);
  }
  66% {
    transform: translateY(20px) rotate(240deg);
  }
}

/* Container */
.container {
  max-width: 800px;
  margin: 50px;
  padding: 20px;
  text-align: center;
  position: relative;
  z-index: 2;
}

/* Error Code with Glitch Effect */
.error-code {
  margin-bottom: 30px;
}

.glitch {
  font-size: 8rem;
  font-weight: 900;
  color: #4ade80;
  position: relative;
  display: inline-block;
  text-shadow: 0 0 20px rgba(74, 222, 128, 0.5);
  animation: glitch 2s infinite;
}

.glitch::before,
.glitch::after {
  content: attr(data-text);
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

.glitch::before {
  animation: glitch-1 0.5s infinite;
  color: #ef4444;
  z-index: -1;
}

.glitch::after {
  animation: glitch-2 0.5s infinite;
  color: #3b82f6;
  z-index: -2;
}

@keyframes glitch {
  0%, 74%, 76%, 80%, 82%, 84%, 100% {
    transform: translate(0);
  }
  75% {
    transform: translate(-2px, 2px);
  }
  77% {
    transform: translate(2px, -2px);
  }
  81% {
    transform: translate(-2px, 2px);
  }
  83% {
    transform: translate(2px, -2px);
  }
}

@keyframes glitch-1 {
  0%, 74%, 76%, 80%, 82%, 84%, 100% {
    transform: translate(0);
  }
  75% {
    transform: translate(2px, -2px);
  }
  77% {
    transform: translate(-2px, 2px);
  }
  81% {
    transform: translate(2px, -2px);
  }
  83% {
    transform: translate(-2px, 2px);
  }
}

@keyframes glitch-2 {
  0%, 74%, 76%, 80%, 82%, 84%, 100% {
    transform: translate(0);
  }
  75% {
    transform: translate(-2px, 2px);
  }
  77% {
    transform: translate(2px, -2px);
  }
  81% {
    transform: translate(-2px, 2px);
  }
  83% {
    transform: translate(2px, -2px);
  }
}

/* Message Section */
.message-section {
  margin-bottom: 40px;
}

.main-title {
  font-size: 3rem;
  font-weight: 700;
  margin-bottom: 10px;
  background: linear-gradient(45deg, #4ade80, #22c55e, #16a34a);
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  animation: shimmer 2s infinite;
}

.subtitle {
  font-size: 1.2rem;
  color: rgba(255, 255, 255, 0.8);
  margin-bottom: 0;
}

@keyframes shimmer {
  0% {
    background-position: -200% center;
  }
  100% {
    background-position: 200% center;
  }
}

/* Interactive Section */
.interactive-section {
  margin-bottom: 40px;
}

.icon-bounce {
  display: inline-block;
  animation: bounce 2s infinite;
  margin-bottom: 20px;
}

.icon-bounce svg {
  filter: drop-shadow(0 4px 8px rgba(74, 222, 128, 0.3));
  transition: all 0.3s ease;
}

.icon-bounce:hover svg {
  transform: scale(1.1);
  filter: drop-shadow(0 6px 12px rgba(74, 222, 128, 0.5));
}

@keyframes bounce {
  0%, 20%, 53%, 80%, 100% {
    transform: translate3d(0,0,0);
  }
  40%, 43% {
    transform: translate3d(0,-30px,0);
  }
  70% {
    transform: translate3d(0,-15px,0);
  }
  90% {
    transform: translate3d(0,-4px,0);
  }
}

.description {
  font-size: 1.1rem;
  color: rgba(255, 255, 255, 0.9);
  line-height: 1.6;
  max-width: 600px;
  margin: 0 auto;
}

/* Action Buttons */
.action-buttons {
  display: flex;
  gap: 20px;
  justify-content: center;
  flex-wrap: wrap;
  margin-bottom: 40px;
}

.btn-primary, .btn-secondary {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 15px 30px;
  border-radius: 50px;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  cursor: pointer;
  border: none;
  font-size: 1rem;
}

.btn-primary {
  background: linear-gradient(45deg, #22c55e, #16a34a);
  color: white;
  box-shadow: 0 4px 15px rgba(34, 197, 94, 0.3);
}

.btn-primary:hover {
  transform: translateY(-3px);
  box-shadow: 0 8px 25px rgba(34, 197, 94, 0.4);
}

.btn-secondary {
  background: transparent;
  color: white;
  border: 2px solid #4ade80;
}

.btn-secondary:hover {
  background: #4ade80;
  color: #0f2419;
  transform: translateY(-3px);
  box-shadow: 0 8px 25px rgba(74, 222, 128, 0.3);
}

.btn-icon {
  font-size: 1.2rem;
}

/* Fun Section */
.fun-section {
  opacity: 0.8;
}

.fun-fact {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 15px 25px;
  background: rgba(74, 222, 128, 0.1);
  border-radius: 25px;
  border: 1px solid rgba(74, 222, 128, 0.2);
}

.fact-icon {
  display: inline-flex;
  animation: pulse 2s infinite;
}

.fact-icon svg {
  transition: all 0.3s ease;
}

.fun-fact:hover .fact-icon svg {
  transform: rotate(360deg);
}

.fact-text {
  font-size: 1rem;
  color: rgba(255, 255, 255, 0.9);
}

@keyframes pulse {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
  100% {
    transform: scale(1);
  }
}

/* Particles */
.particles {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
}

.particle {
  position: absolute;
  width: 4px;
  height: 4px;
  background: #4ade80;
  border-radius: 50%;
  animation: particle-float 8s infinite linear;
}

.particle:nth-child(odd) {
  background: #22c55e;
  animation-duration: 6s;
}

.particle:nth-child(3n) {
  background: #16a34a;
  animation-duration: 10s;
}

@keyframes particle-float {
  0% {
    transform: translateY(100vh) translateX(-50px) rotate(0deg);
    opacity: 0;
  }
  10% {
    opacity: 1;
  }
  90% {
    opacity: 1;
  }
  100% {
    transform: translateY(-100px) translateX(50px) rotate(360deg);
    opacity: 0;
  }
}

/* Position particles randomly */
.particle:nth-child(1) { left: 10%; animation-delay: 0s; }
.particle:nth-child(2) { left: 20%; animation-delay: 1s; }
.particle:nth-child(3) { left: 30%; animation-delay: 2s; }
.particle:nth-child(4) { left: 40%; animation-delay: 3s; }
.particle:nth-child(5) { left: 50%; animation-delay: 4s; }
.particle:nth-child(6) { left: 60%; animation-delay: 5s; }
.particle:nth-child(7) { left: 70%; animation-delay: 6s; }
.particle:nth-child(8) { left: 80%; animation-delay: 7s; }
.particle:nth-child(9) { left: 90%; animation-delay: 8s; }
.particle:nth-child(10) { left: 15%; animation-delay: 9s; }
.particle:nth-child(11) { left: 25%; animation-delay: 10s; }
.particle:nth-child(12) { left: 35%; animation-delay: 11s; }
.particle:nth-child(13) { left: 45%; animation-delay: 12s; }
.particle:nth-child(14) { left: 55%; animation-delay: 13s; }
.particle:nth-child(15) { left: 65%; animation-delay: 14s; }
.particle:nth-child(16) { left: 75%; animation-delay: 15s; }
.particle:nth-child(17) { left: 85%; animation-delay: 16s; }
.particle:nth-child(18) { left: 5%; animation-delay: 17s; }
.particle:nth-child(19) { left: 95%; animation-delay: 18s; }
.particle:nth-child(20) { left: 50%; animation-delay: 19s; }

/* Sparkle Effect */
.sparkle {
  position: absolute;
  width: 6px;
  height: 6px;
  background: #fbbf24;
  border-radius: 50%;
  pointer-events: none;
  animation: sparkle-animation 2s ease-out forwards;
}

@keyframes sparkle-animation {
  0% {
    transform: scale(0) rotate(0deg);
    opacity: 1;
  }
  50% {
    transform: scale(1) rotate(180deg);
    opacity: 1;
  }
  100% {
    transform: scale(0) rotate(360deg);
    opacity: 0;
  }
}

/* Responsive Design */
@media (max-width: 768px) {
  .glitch {
    font-size: 5rem;
  }

  .main-title {
    font-size: 2rem;
  }

  .emoji-bounce {
    font-size: 3rem;
  }

  .action-buttons {
    flex-direction: column;
    align-items: center;
  }

  .btn-primary, .btn-secondary {
    width: 200px;
    justify-content: center;
  }
}

@media (max-width: 480px) {
  .container {
    padding: 15px;
  }

  .glitch {
    font-size: 4rem;
  }

  .main-title {
    font-size: 1.8rem;
  }

  .subtitle {
    font-size: 1rem;
  }

  .description {
    font-size: 1rem;
  }
}
</style>